import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { 
  LogOut, Palette, GraduationCap, Lock, CheckCircle, 
  ChevronRight, Volume2, BookOpen, MessageSquare, Star,
  Music, Globe, Settings, Crown, Target, Flame, BookMarked, UserCircle
} from 'lucide-react';
import { toast } from 'sonner';
import ScriptToggle from '../components/ScriptToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewDashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  
  const [levels, setLevels] = useState([]);
  const [userProgress, setUserProgress] = useState({ modules: [], levels: [] });
  const [stats, setStats] = useState({ vocabulary_count: 0, lesson_count: 0 });
  const [loading, setLoading] = useState(true);
  const [soundsCompleted, setSoundsCompleted] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    if (!user || !token) return;
    axios.get(`${API}/user/words-to-review`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setReviewCount(res.data?.words?.length || 0))
      .catch(() => setReviewCount(0));
  }, [user, token]);

  // Refresh user data on mount to get updated streak
  useEffect(() => {
    if (user && refreshUser) {
      refreshUser();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // GUEST MODE: Allow access without authentication
    // Check if sounds module is completed (works for guests too)
    const soundsDone = localStorage.getItem('darija_sounds_completed') === 'true';
    setSoundsCompleted(soundsDone);
    
    // Check premium status only if user is logged in
    if (user) {
      const checkPremiumStatus = async () => {
        // Admin always has premium
        if (user?.is_admin) {
          setIsPremiumUser(true);
          return;
        }
        
        // Check from user object first
        if (user?.is_premium) {
          setIsPremiumUser(true);
          return;
        }
        
        // Fallback: fetch from API
        if (token) {
          try {
            const response = await axios.get(`${API}/premium/status`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setIsPremiumUser(response.data.is_premium || false);
          } catch (error) {
            console.log('Could not fetch premium status');
            setIsPremiumUser(false);
          }
        }
      };
      checkPremiumStatus();
    }
    
    fetchData();
  }, [user, token]);

  const fetchData = async () => {
    try {
      // Try to load cached data first for instant display
      const cachedLevels = localStorage.getItem('morocotalk_levels');
      const cachedStats = localStorage.getItem('morocotalk_stats');
      
      if (cachedLevels && cachedStats) {
        setLevels(JSON.parse(cachedLevels));
        setStats(JSON.parse(cachedStats));
        setLoading(false); // Show cached data immediately
      }
      
      // Fetch fresh data from server
      const [levelsRes, statsRes, progressRes, levelProgressRes] = await Promise.all([
        axios.get(`${API}/levels`),
        axios.get(`${API}/stats`),
        token ? axios.get(`${API}/user-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { modules: [], levels: [] } })) : Promise.resolve({ data: { modules: [], levels: [] } }),
        token ? axios.get(`${API}/user/level-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { unlocked_levels: { beginner: true }, level_stats: {} } })) : Promise.resolve({ data: { unlocked_levels: { beginner: true }, level_stats: {} } })
      ]);
      
      // Update state with fresh data
      setLevels(levelsRes.data);
      setStats(statsRes.data);
      setUserProgress({
        ...progressRes.data,
        ...levelProgressRes.data
      });
      
      // Cache the data for offline/fast reload
      localStorage.setItem('morocotalk_levels', JSON.stringify(levelsRes.data));
      localStorage.setItem('morocotalk_stats', JSON.stringify(statsRes.data));
      localStorage.setItem('morocotalk_cache_time', Date.now().toString());
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      
      // If server is unavailable, use cached data
      const cachedLevels = localStorage.getItem('morocotalk_levels');
      const cachedStats = localStorage.getItem('morocotalk_stats');
      
      if (cachedLevels && cachedStats) {
        setLevels(JSON.parse(cachedLevels));
        setStats(JSON.parse(cachedStats));
        toast.info(language === 'en' 
          ? 'Using cached data. Some features may be limited.' 
          : 'Utilisation des données en cache. Certaines fonctionnalités peuvent être limitées.');
      } else {
        toast.error(language === 'en' 
          ? 'Could not load data. Please check your connection.' 
          : 'Impossible de charger les données. Vérifiez votre connexion.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success(language === 'en' ? 'Logged out successfully' : 'Déconnecté avec succès');
  };

  const handleLevelClick = (level, index) => {
    // ADMIN BYPASS: Admins have access to ALL levels without restrictions
    if (user?.is_admin) {
      navigate(`/level/${level.id}`);
      return;
    }
    
    // LEVEL 1: Always accessible (free categories inside, premium categories locked)
    if (index === 0) {
      navigate(`/level/${level.id}`);
      return;
    }
    
    // LEVELS 2 & 3: Require Premium subscription
    if (!isPremiumUser) {
      toast.error(language === 'en' 
        ? '🔒 Premium content! Subscribe to unlock all levels.' 
        : '🔒 Contenu Premium ! Abonnez-vous pour débloquer tous les niveaux.');
      navigate('/premium');
      return;
    }
    
    // LEVELS 2 & 3: Check if level is unlocked (from Grand Quiz completion)
    const levelKey = index === 1 ? 'intermediate' : 'advanced';
    const isLevelUnlockedByQuiz = userProgress.unlocked_levels?.[levelKey] === true;
    
    if (!isLevelUnlockedByQuiz) {
      // Check the previous level's Grand Quiz score
      const previousLevelKey = index === 1 ? 'beginner' : 'intermediate';
      const prevLevelStats = userProgress.level_stats?.[previousLevelKey];
      const grandQuizPassed = prevLevelStats?.grand_quiz_passed === true;
      const grandQuizScore = prevLevelStats?.grand_quiz_score || 0;
      
      if (!grandQuizPassed) {
        toast.error(language === 'en' 
          ? `🎯 Pass the Grand Quiz of the previous level with 80%+!` 
          : `🎯 Réussissez le Grand Quiz du niveau précédent avec 80%+ !`);
        return;
      }
    }
    
    // Premium user with previous level completed can access
    navigate(`/level/${level.id}`);
  };
  
  const isLevelUnlocked = (levelId, index) => {
    // ADMIN BYPASS: All levels unlocked for admins
    if (user?.is_admin) return true;
    
    if (index === 0) return true;
    
    // Check user progress from API
    const levelProgress = userProgress.levels?.find(p => p.level_id === levelId);
    if (levelProgress?.unlocked) return true;
    
    // Also check unlocked_levels from level-progress API
    const levelMap = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
    const levelKey = levelMap[index + 1];
    if (userProgress.unlocked_levels?.[levelKey]) return true;
    
    return false;
  };

  const getLevelIcon = (levelNumber) => {
    const icons = ['🌱', '🌿', '🌳'];
    return icons[levelNumber - 1] || '🌟';
  };

  const getLevelColor = (levelNumber) => {
    const colors = [
      'from-emerald-400 to-emerald-600',
      'from-blue-400 to-blue-600',
      'from-purple-400 to-purple-600'
    ];
    return colors[levelNumber - 1] || 'from-gray-400 to-gray-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <p className="font-nunito text-xl text-charcoal">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      {/* Header - Compact and sticky */}
      <nav className="bg-white border-b border-stone-200 shadow-sm sticky top-0 z-50">
        <div className="px-3 py-1.5 sm:px-6 sm:py-2.5 flex justify-between items-center gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <img src="/logo.svg" alt="MoroccoTalk" className="w-7 h-7 sm:w-9 sm:h-9 flex-shrink-0" />
            <h1 className="font-playfair text-base sm:text-2xl font-bold text-primary truncate">MoroccoTalk</h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {user && (
              <div data-testid="streak-indicator" className="flex flex-col items-center leading-none">
                <div className="flex items-center gap-0.5 px-1.5 py-0.5 sm:px-2 sm:py-1 bg-orange-50 rounded-md border border-orange-200">
                  <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500" />
                  <span className="text-xs sm:text-sm font-nunito font-bold text-orange-600">{user.current_streak || 0}</span>
                </div>
                <span data-testid="streak-message" className="mt-0.5 text-[9px] sm:text-[10px] font-nunito text-orange-600/80 whitespace-nowrap">
                  {(user.current_streak || 0) > 0
                    ? (language === 'en' ? 'Back tomorrow!' : 'Reviens demain !')
                    : (language === 'en' ? 'Start today!' : 'Commence !')}
                </span>
              </div>
            )}

            {user && (
              <button
                onClick={() => navigate('/review')}
                data-testid="review-btn"
                className="relative p-1.5 sm:p-2 bg-violet-500 text-white rounded-md shadow-sm"
                title={language === 'en' ? 'Words to Review' : 'Mots à réviser'}
              >
                <BookMarked className="w-4 h-4" />
                {reviewCount > 0 && (
                  <span data-testid="review-count-badge" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-nunito font-bold rounded-full flex items-center justify-center leading-none">
                    {reviewCount > 99 ? '99+' : reviewCount}
                  </span>
                )}
              </button>
            )}

            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-1.5 sm:p-2 bg-destructive text-white rounded-md shadow-sm"
              title={language === 'en' ? 'Logout' : 'Déconnexion'}
            >
              <LogOut className="w-4 h-4" />
            </button>

            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                data-testid="profile-menu-btn"
                className="p-1 text-charcoal/70 hover:text-primary rounded-full transition-colors"
                title={language === 'en' ? 'Menu' : 'Menu'}
              >
                <UserCircle className="w-7 h-7" />
              </button>
              {showProfileMenu && (
                <div data-testid="profile-menu" className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-50">
                  <p className="px-4 pb-2 mb-1 border-b border-stone-200 font-nunito text-sm font-bold text-charcoal truncate">
                    {user?.name || (language === 'en' ? 'Guest' : 'Invité')}
                  </p>
                  <div className="px-4 py-2 flex justify-center">
                    <ScriptToggle />
                  </div>
                  <button
                    onClick={() => { setShowProfileMenu(false); navigate('/premium'); }}
                    data-testid="premium-btn"
                    className="w-full px-4 py-2 text-left text-sm font-nunito text-charcoal hover:bg-amber-50 flex items-center gap-2 transition-colors"
                  >
                    <Crown className="w-4 h-4 text-amber-500" />
                    Premium
                  </button>
                  {user?.is_admin && (
                    <button
                      onClick={() => { setShowProfileMenu(false); navigate('/admin'); }}
                      data-testid="admin-btn"
                      className="w-full px-4 py-2 text-left text-sm font-nunito text-charcoal hover:bg-sand flex items-center gap-2 transition-colors"
                    >
                      <Palette className="w-4 h-4 text-primary" />
                      Admin
                    </button>
                  )}
                  <button
                    onClick={() => { setShowProfileMenu(false); navigate('/settings'); }}
                    data-testid="settings-btn"
                    className="w-full px-4 py-2 text-left text-sm font-nunito text-charcoal hover:bg-sand flex items-center gap-2 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-charcoal/70" />
                    {language === 'en' ? 'Settings' : 'Paramètres'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content - flex-1 to push stats to bottom */}
      <div className="flex-1 flex flex-col justify-evenly gap-3 px-3 pt-3 pb-20 sm:px-6 sm:pt-6 sm:pb-24 sm:gap-6">
        {/* Welcome - compact */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="font-playfair text-lg leading-tight sm:text-3xl font-bold text-charcoal">
            {t('hello')}, {user?.name || (language === 'en' ? 'Guest' : 'Invité')}! 🌟
          </h2>
          <p className="font-nunito text-xs sm:text-base text-charcoal/70">
            {t('chooseLevel')}
          </p>
        </motion.div>

        {/* Introduction Module - Sounds of Darija - compact */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <h3 className="font-playfair text-sm sm:text-xl font-bold text-charcoal mb-1 sm:mb-2 flex items-center gap-1.5">
            <Music className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            Introduction
          </h3>
          
          <button
            onClick={() => navigate('/sounds')}
            data-testid="sounds-module-btn"
            className={`w-full tile-button rounded-xl px-3 py-3 sm:p-5 shadow-md text-left transition-all ${
              soundsCompleted
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
                : 'bg-gradient-to-r from-primary to-orange-600 text-white ring-2 ring-primary/30'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="font-nunito font-bold text-sm sm:text-lg">
                    {t('soundsTitle')}
                  </h4>
                  <p className="font-nunito text-xs sm:text-sm opacity-90">
                    {soundsCompleted 
                      ? (language === 'en' ? 'Completed ✓' : 'Terminé ✓')
                      : t('startHere')}
                  </p>
                </div>
              </div>
              {soundsCompleted ? (
                <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7" />
              ) : (
                <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
              )}
            </div>
          </button>
        </motion.div>

        {user && reviewCount > 0 && (
          <div data-testid="review-reminder-banner" className="flex items-center justify-between gap-2 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
              <BookMarked className="w-4 h-4 text-violet-600 flex-shrink-0" />
              <p className="font-nunito text-xs sm:text-sm font-semibold text-violet-800 truncate">
                {language === 'en'
                  ? `You have ${reviewCount} word${reviewCount > 1 ? 's' : ''} to review`
                  : `Tu as ${reviewCount} mot${reviewCount > 1 ? 's' : ''} à réviser`}
              </p>
            </div>
            <button
              onClick={() => navigate('/review')}
              data-testid="review-reminder-btn"
              className="flex-shrink-0 px-3 py-1 bg-violet-500 hover:bg-violet-600 text-white text-xs font-nunito font-bold rounded-md transition-colors"
            >
              {language === 'en' ? 'Review' : 'Réviser'}
            </button>
          </div>
        )}

        {/* Levels - compact cards */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="font-playfair text-sm sm:text-xl font-bold text-charcoal mb-1 sm:mb-2 flex items-center gap-1.5">
            <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            {language === 'en' ? 'Learning Path' : 'Parcours'}
          </h3>
          
          <div className="space-y-2 sm:space-y-3">
            {levels.map((level, index) => {
              // FREEMIUM LOGIC:
              const isPremiumLocked = index > 0 && !isPremiumUser && !user?.is_admin;
              const levelKey = index === 0 ? 'beginner' : (index === 1 ? 'intermediate' : 'advanced');
              const previousLevelKey = index === 1 ? 'beginner' : 'intermediate';
              const isLevelUnlockedByQuiz = index === 0 || userProgress.unlocked_levels?.[levelKey] === true;
              const prevGrandQuizPassed = userProgress.level_stats?.[previousLevelKey]?.grand_quiz_passed === true;
              const isScoreLocked = index > 0 && isPremiumUser && !isLevelUnlockedByQuiz && !prevGrandQuizPassed && !user?.is_admin;
              const isUnlocked = index === 0 || (isPremiumUser && (isLevelUnlockedByQuiz || prevGrandQuizPassed)) || user?.is_admin;
              
              const levelTitle = language === 'en' ? level.title_en : level.title_fr;
              
              return (
                <motion.button
                  key={level.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + index * 0.05 }}
                  onClick={() => handleLevelClick(level, index)}
                  data-testid={`level-${level.level_number}-btn`}
                  data-level-key={levelKey}
                  className={`w-full tile-button rounded-xl px-3 py-3 sm:p-5 shadow-md text-left transition-all ${
                    isUnlocked
                      ? `bg-gradient-to-r ${getLevelColor(level.level_number)} text-white`
                      : isPremiumLocked
                        ? 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300'
                        : isScoreLocked
                          ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300'
                          : 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${
                        isUnlocked ? 'bg-white/20' : isPremiumLocked ? 'bg-amber-400/30' : isScoreLocked ? 'bg-blue-400/30' : 'bg-stone-400/30'
                      }`}>
                        {isPremiumLocked || isScoreLocked ? (
                          <Lock className={`w-5 h-5 sm:w-6 sm:h-6 ${isScoreLocked ? 'text-blue-600' : 'text-amber-600'}`} />
                        ) : (
                          <span className="text-lg sm:text-2xl">{getLevelIcon(level.level_number)}</span>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <h4 className="font-nunito font-bold text-sm sm:text-lg">
                            {levelTitle}
                          </h4>
                          {isPremiumLocked && (
                            <span className="bg-amber-500 text-white px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-nunito font-bold flex items-center gap-0.5">
                              <Crown className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              Premium
                            </span>
                          )}
                          {isScoreLocked && (
                            <span className="bg-blue-500 text-white px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-nunito font-bold">
                              Quiz
                            </span>
                          )}
                        </div>
                        <p className="font-nunito text-[10px] sm:text-xs opacity-80">
                          {isPremiumLocked 
                            ? (language === 'en' ? '🔒 Subscribe to unlock' : '🔒 Abonnez-vous')
                            : isScoreLocked
                              ? (language === 'en' ? '🎯 Pass previous Grand Quiz' : '🎯 Grand Quiz requis')
                              : t('requiredScore')}
                        </p>
                      </div>
                    </div>
                    {isPremiumLocked ? (
                      <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
                    ) : isScoreLocked ? (
                      <Target className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Stats - compact footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white border-t border-stone-200 px-3 py-1.5 sm:px-6 sm:py-4"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <GraduationCap className="w-4 h-4 text-primary" />
            <span className="font-nunito text-xs sm:text-sm font-bold text-charcoal">{language === 'en' ? 'Content' : 'Contenu'}</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="text-center">
              <p className="font-nunito text-xs sm:text-lg font-bold text-primary">+1500</p>
              <p className="font-nunito text-[9px] sm:text-xs text-charcoal/60 leading-tight">{t('words')}</p>
            </div>
            <div className="text-center">
              <p className="font-nunito text-xs sm:text-lg font-bold text-accent">+600</p>
              <p className="font-nunito text-[9px] sm:text-xs text-charcoal/60 leading-tight">Quiz</p>
            </div>
            <div className="text-center">
              <p className="font-nunito text-xs sm:text-lg font-bold text-secondary">+120</p>
              <p className="font-nunito text-[9px] sm:text-xs text-charcoal/60 leading-tight">Situations</p>
            </div>
            <div className="text-center">
              <p className="font-nunito text-xs sm:text-lg font-bold text-oasis">+75</p>
              <p className="font-nunito text-[9px] sm:text-xs text-charcoal/60 leading-tight">{t('lessons')}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {showProfileMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
      )}

      {/* Floating Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: 'spring' }}
        onClick={() => navigate('/chat')}
        data-testid="floating-chat-btn"
        className="fixed bottom-16 right-4 sm:bottom-20 sm:right-6 w-12 h-12 sm:w-14 sm:h-14 bg-oasis text-white rounded-full shadow-lg flex items-center justify-center hover:bg-oasis/90 transition-colors z-40"
      >
        <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.button>

    </div>
  );
};

export default NewDashboard;
